import palette from "assets/theme/base/palette";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Grid from "@mui/material/Grid";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import { CircularProgress } from "@mui/material";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DataTable from "examples/Tables/DataTable";

import { useNavigate, useLocation } from "react-router-dom";
import AthleteOnBoardingScreen from "./AthleteOnBoardingScreen";

import React, { useEffect, useState } from "react";
import { call } from "services/axiosService/core";
import { useAuthenticator } from "@aws-amplify/ui-react";
import { useMaterialUIController } from "context";
import Loader from "pages/loader/Loader";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import * as Sentry from "@sentry/browser";
import Bot from "pages/bot/Bot";
import { useTranslation } from "react-i18next";

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const MiniWave = ({ color }) => (
  <svg width="110" height="26" viewBox="0 0 110 26" fill="none">
    <path d="M0 13 Q13.75 4 27.5 13 Q41.25 22 55 13 Q68.75 4 82.5 13 Q96.25 22 110 13"
      stroke={color} strokeWidth="1.8" fill="none" strokeLinecap="round" opacity="0.7" />
  </svg>
);

const DonutChart = ({ segments, total, centerLabel }) => {
  const validTotal = Math.max(total, 1);
  let cumulative = 0;
  const stops = segments
    .filter((s) => s.value > 0)
    .map((s) => {
      const pct = (s.value / validTotal) * 100;
      const stop = `${s.color} ${cumulative.toFixed(2)}% ${(cumulative + pct).toFixed(2)}%`;
      cumulative += pct;
      return stop;
    });
  const gradient = stops.length
    ? `conic-gradient(${stops.join(", ")})`
    : `conic-gradient(${palette.neutral[200]} 0% 100%)`;
  return (
    <MDBox sx={{ position: "relative", width: 130, height: 130, flexShrink: 0 }}>
      <MDBox sx={{ width: 130, height: 130, borderRadius: "50%", background: gradient }} />
      <MDBox sx={{
        position: "absolute", top: "50%", left: "50%",
        transform: "translate(-50%, -50%)",
        width: 82, height: 82, borderRadius: "50%", bgcolor: "#fff",
        display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
      }}>
        <MDTypography variant="h5" fontWeight="bold" lineHeight={1}>{total}</MDTypography>
        <MDTypography variant="caption" color="text.secondary" textAlign="center"
          sx={{ fontSize: "0.58rem", lineHeight: 1.3, mt: 0.25, px: 0.5 }}>
          {centerLabel}
        </MDTypography>
      </MDBox>
    </MDBox>
  );
};

const assessmentStatusChip = (rowStatus, label) => {
  const map = {
    ready:         { bg: `${palette.brand[500]}18`,  color: palette.brand[500] },
    in_progress:   { bg: palette.purple[50],         color: palette.purple[500] },
    not_started:   { bg: palette.orange[50],         color: palette.orange[500] },
    scoring:       { bg: palette.green[50],          color: palette.green[500] },
    submitted:     { bg: palette.green[50],          color: palette.green[500] },
  };
  const c = map[rowStatus] || { bg: palette.neutral[100], color: palette.neutral[400] };
  return (
    <Chip label={label} size="small"
      sx={{ bgcolor: c.bg, color: c.color, fontWeight: "bold", fontSize: "0.68rem", height: 22 }} />
  );
};

const ViewInsightsButton = ({ id, user_id, t }) => {
  const navigate = useNavigate();
  return (
    <Tooltip title={<h2>{t("toolTips.viewInsights")}</h2>}>
      <MDTypography variant="body1" color="secondary" lineHeight={1} sx={{ cursor: "pointer", mx: 1 }}>
        <Icon fontSize="medium" color="inherit" onClick={() => navigate(`/insight/${id}/${user_id}`)}>
          psychology
        </Icon>
      </MDTypography>
    </Tooltip>
  );
};

const TakeAssessmentButton = ({ id, t }) => {
  const navigate = useNavigate();
  return (
    <Tooltip title={<h2>{t("toolTips.takeAssessment")}</h2>}>
      <MDTypography variant="body1" color="secondary" lineHeight={1} sx={{ cursor: "pointer", mx: 1 }}>
        <Icon fontSize="medium" color="inherit" onClick={() => navigate(`/assessment/${id}`)}>
          description
        </Icon>
      </MDTypography>
    </Tooltip>
  );
};

const ResumeAssessmentButton = ({ id, t }) => {
  const navigate = useNavigate();
  return (
    <Tooltip title={<h2>{t("toolTips.resumeAssessment")}</h2>}>
      <MDTypography variant="body1" color="secondary" lineHeight={1} sx={{ cursor: "pointer", mx: 1 }}>
        <Icon fontSize="medium" color="inherit" onClick={() => navigate(`/assessment/${id}`)}>
          description
        </Icon>
      </MDTypography>
    </Tooltip>
  );
};

function formatToMMDDYYYY(dateStr) {
  if (!dateStr) return "–";
  const [year, month, day] = dateStr.split("-");
  return `${month}-${day}-${year}`;
}

export function AthleteDashboard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const openBot = location.state?.openBot || false;
  const { user, signOut } = useAuthenticator((context) => [context.user]);
  const [assessments, setAssessments] = useState([]);
  const [resumeAssessment, setResumeAssessment] = useState(null);
  const [controller] = useMaterialUIController();
  const [openLoader, setOpenLoader] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [pageNumber, setPageNumber] = useState(0);
  const [showInsightBanner, setShowInsightBanner] = useState(false);
  const { profile, persona } = controller;

  const [showBot, setShowBot] = useState(false);
  const [autoExpandBot, setAutoExpandBot] = useState(openBot);
  const [showUnreadChat, setShowUnreadChat] = useState(false);
  const [showTodayContent, setShowTodayContent] = useState(false);
  const [todayContent, setTodayContent] = useState([]);
  const [chatQuestions, setChatQuestions] = useState([]);
  const [showIntro, setShowIntro] = useState(false);
  const [userMetricsData, setUserMetricsData] = useState(null);
  const [loader, setLoader] = useState(true);

  const handleCloseSnackbar = (event, reason) => {
    if (reason === "clickaway") return;
    setOpenSnackbar(false);
  };

  /* ── computed counts ── */
  const completedCount    = assessments.filter((a) => a.status === "ready").length;
  const inProgressCount   = assessments.filter((a) => a.status === "in_progress").length;
  const notStartedCount   = assessments.filter((a) => a.status === "not_started" || a.status === "not started").length;
  const scoringCount      = assessments.filter((a) => a.status === "scoring" || a.status === "submitted").length;
  const totalCount        = assessments.length;

  const statCards = [
    { icon: "assignment",             color: palette.brand[500],   bg: `${palette.brand[500]}18`, count: totalCount,     label: t("cardTitles.totalAssessments"),     sub: t("messages.dashboard.acrossAllPrograms") },
    { icon: "lightbulb",              color: palette.brand[500],   bg: `${palette.brand[500]}18`, count: completedCount,  label: t("cardTitles.completedAssessments"),  sub: t("messages.dashboard.insightsAvailable") },
    { icon: "pending",                color: palette.purple[500],  bg: palette.purple[50],        count: inProgressCount, label: t("cardTitles.inProgressAssessments"), sub: t("messages.dashboard.continueWhereLeft") },
    { icon: "radio_button_unchecked", color: palette.orange[500],  bg: palette.orange[50],        count: notStartedCount, label: t("cardTitles.notStartedAssessments"), sub: t("messages.dashboard.readyToBegin") },
  ];

  const quickLinks = [
    { icon: "menu_book",     color: palette.brand[500],  bg: `${palette.brand[500]}18`, label: t("routes.contentLibrary"), onClick: () => navigate("/contentlibrary") },
    { icon: "play_circle",   color: palette.green[500],  bg: palette.green[50],         label: t("routes.myContent"),       onClick: () => navigate("/playlist") },
    { icon: "track_changes", color: palette.purple[500], bg: palette.purple[50],        label: t("routes.myGamePlan"),      onClick: () => navigate("/mygameplan") },
    { icon: "person",        color: palette.orange[500], bg: palette.orange[50],        label: t("routes.profile"),         onClick: () => navigate("/profile") },
  ];

  const tableData = {
    columns: [
      { Header: t("tableHeaders.name"),           accessor: "name" },
      { Header: t("tableHeaders.assignedDate"),   accessor: "assigned_date" },
      { Header: t("tableHeaders.status"),         accessor: "status" },
      { Header: t("tableHeaders.completionDate"), accessor: "completion_date" },
      { Header: t("tableHeaders.actions"),        accessor: "actions", width: "12%" },
    ],
    rows: assessments.map((row) => {
      const rowStatus = row.status;
      let statusLabel = t(`userDynamicData.${row.status}`);
      let action;
      if (row.status === "not_started" || row.status === "not started") {
        action = <TakeAssessmentButton id={row.id} t={t} />;
      } else if (row.status === "in_progress") {
        action = <ResumeAssessmentButton id={row.id} t={t} />;
      } else if (row.status === "ready") {
        action = <ViewInsightsButton id={row.id} user_id={row.user_id} t={t} />;
      } else {
        action = "";
      }

      return {
        id: row.id,
        name: row.details.name,
        assigned_date: (
          <MDTypography variant="caption" color="text.secondary">
            {formatToMMDDYYYY(row.assigned_date)}
          </MDTypography>
        ),
        status: assessmentStatusChip(rowStatus, statusLabel),
        completion_date: (
          <MDTypography variant="caption" color="text.secondary">
            {formatToMMDDYYYY(row.completion_date)}
          </MDTypography>
        ),
        actions: action,
      };
    }),
  };

  useEffect(() => {
    if (persona === "athlete") {
      loadDailyChatQuestions();
      getUnreadStatus();
      getDailyContent();
      getIntroMessageStatus();
      loadUserContentMetrics();
    }
  }, [persona]);

  const loadUserContentMetrics = () => {
    call(`users/${profile.id}/contentmetrics`, "GET", {})
      .then((res) => {
        if (res.status === 200) setUserMetricsData(res.data);
      })
      .catch((err) => Sentry.captureException(err));
  };

  const loadDailyChatQuestions = () => {
    call(`chats/bot`, "GET", {})
      .then((res) => {
        if (res.status === 200) {
          setChatQuestions(res.data.map((item) => ({ ...item, userResponse: "" })));
        } else {
          setChatQuestions([]);
        }
      })
      .catch((err) => Sentry.captureException(err));
  };

  const getUnreadStatus = () => {
    const currentDate = new Date().toLocaleDateString();
    if (currentDate !== localStorage.getItem("lastBotViewed")) setShowUnreadChat(true);
  };

  const getIntroMessageStatus = () => {
    const currentDate = new Date().toLocaleDateString();
    if (currentDate !== localStorage.getItem("IntroMessageViewed")) setShowIntro(true);
  };

  const getDailyContent = () => {
    const currentDate = new Date().toLocaleDateString();
    if (currentDate === localStorage.getItem("lastContentUpdatedDate")) {
      const dailyContent = JSON.parse(localStorage.getItem("dailyContent")) || null;
      if (dailyContent) { setShowTodayContent(true); setTodayContent(dailyContent); }
    }
  };

  useEffect(() => {
    if (profile.status === "active") loadingAssessmentsHandler();
  }, []);

  const loadingAssessmentsHandler = () => {
    if (!profile.id) return;
    setLoader(true);
    call(`users/${profile.id}/assessments`, "GET", {})
      .then((res) => {
        if (res.status === 200) {
          const data = res.data.Assessments;
          setAssessments(data);
          const pending = data.find(
            (a) => a.status === "not_started" || a.status === "in_progress" || a.status === "not started"
          );
          if (pending) setResumeAssessment(pending);
          const hasCompleted = data.some((a) => a.status === "ready");
          if (hasCompleted) setShowBot(true);
        } else if (res.status === 404) {
          setAssessments([]);
          setOpenSnackbar(true);
        }
        setLoader(false);
      })
      .catch((err) => {
        Sentry.captureException(err);
        setLoader(false);
      });
  };

  return (
    <DashboardLayout>
      <AthleteOnBoardingScreen role="athlete" setOpenLoader={setOpenLoader} />
      <DashboardNavbar title={t("pageTitles.assessmentDashboard")} />

      {openLoader && (
        <LoadingAssessmentDialog
          openLoader={openLoader}
          setOpenLoader={setOpenLoader}
          loadingAssessmentsHandler={loadingAssessmentsHandler}
          t={t}
        />
      )}

      {/* Banner: pending assessment */}
      {resumeAssessment && (
        <MDBox mt={2.5}
          sx={{
            display: "flex", alignItems: "center", gap: 2,
            px: 2.5, py: 1.75, borderRadius: 3,
            bgcolor: `${palette.brand[500]}12`,
            border: `1px solid ${palette.brand[200]}`,
          }}
        >
          <MDBox sx={{ width: 44, height: 44, borderRadius: "12px", bgcolor: `${palette.brand[500]}20`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <Icon sx={{ color: palette.brand[500], fontSize: "22px !important" }}>assignment_late</Icon>
          </MDBox>
          <MDBox flex={1} minWidth={0}>
            <MDTypography variant="body2" fontWeight="bold" sx={{ color: palette.brand[700] || palette.brand[600], lineHeight: 1.3 }}>
              {t("notifications.newAssessment.welcome", { name: profile.details.name.split(" ")[0] })}{" "}
              <MDTypography
                component="a"
                href={`/assessment/${resumeAssessment.id}`}
                variant="body2"
                fontWeight="bold"
                sx={{ color: palette.brand[600], textDecoration: "underline" }}
              >
                {resumeAssessment.details?.name || t("notifications.newAssessment.assessment")}
              </MDTypography>{" "}
              {t("notifications.newAssessment.assigned")}
            </MDTypography>
            <MDTypography variant="caption" sx={{ color: palette.brand[500] }}>
              {t("notifications.newAssessment.instruction")}{" "}
              <a href={`/assessment/${resumeAssessment.id}`} style={{ color: "inherit", fontWeight: "bold" }}>
                {t("notifications.newAssessment.clickHere")}
              </a>{" "}
              {t("notifications.newAssessment.instructionContinuation")}
            </MDTypography>
          </MDBox>
          <IconButton size="small" onClick={() => setResumeAssessment(null)}
            sx={{ color: palette.brand[500], flexShrink: 0, "&:hover": { bgcolor: `${palette.brand[500]}18` } }}>
            <Icon sx={{ fontSize: "18px !important" }}>close</Icon>
          </IconButton>
        </MDBox>
      )}

      <MDBox mt={3}>
        {/* ── 4 stat cards ── */}
        <Grid container spacing={2.5} mb={3}>
          {statCards.map((card, i) => (
            <Grid item xs={12} sm={6} lg={3} key={i}>
              <Card sx={{ p: 2.5, borderRadius: 3, boxShadow: "0 2px 12px rgba(0,0,0,0.07)", transition: "all 200ms" }}>
                <MDBox display="flex" justifyContent="space-between" alignItems="flex-start" mb={1.5}>
                  <MDBox sx={{ width: 52, height: 52, borderRadius: "16px", bgcolor: card.bg, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <Icon sx={{ color: card.color, fontSize: "28px !important" }}>{card.icon}</Icon>
                  </MDBox>
                </MDBox>
                {loader ? (
                  <CircularProgress size={22} sx={{ color: card.color, mb: 0.5 }} />
                ) : (
                  <MDTypography variant="h3" fontWeight="bold" sx={{ color: card.color, lineHeight: 1 }}>{card.count}</MDTypography>
                )}
                <MDTypography variant="body2" fontWeight="bold" mt={0.5}>{card.label}</MDTypography>
                <MDTypography variant="caption" color="text.secondary">{card.sub}</MDTypography>
                <MDBox mt={1.5}><MiniWave color={card.color} /></MDBox>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* ── Assessment Overview + Quick Links ── */}
        <Grid container spacing={2.5} mb={3}>
          {/* Assessment Overview */}
          <Grid item xs={12} md={6}>
            <Card sx={{ p: 3, borderRadius: 3, height: "100%", boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}>
              <MDTypography variant="h6" fontWeight="bold" mb={2.5}>{t("headings.assessmentOverview")}</MDTypography>
              {loader ? (
                <MDBox display="flex" justifyContent="center" py={5}><CircularProgress sx={{ color: palette.brand[500] }} /></MDBox>
              ) : (
                <MDBox display="flex" alignItems="center" gap={3} flexWrap="wrap">
                  <DonutChart
                    segments={[
                      { color: palette.brand[500],  value: completedCount },
                      { color: palette.purple[500], value: inProgressCount },
                      { color: palette.orange[500], value: notStartedCount },
                      { color: palette.green[500],  value: scoringCount },
                    ]}
                    total={totalCount}
                    centerLabel={t("cardTitles.totalAssessments")}
                  />
                  <MDBox flex={1} minWidth={120}>
                    {[
                      { label: t("cardTitles.completedAssessments"),  value: completedCount,  color: palette.brand[500] },
                      { label: t("cardTitles.inProgressAssessments"), value: inProgressCount, color: palette.purple[500] },
                      { label: t("cardTitles.notStartedAssessments"), value: notStartedCount, color: palette.orange[500] },
                      { label: t("userDynamicData.scoring"),          value: scoringCount,    color: palette.green[500] },
                    ].map((item) => (
                      <MDBox key={item.label} display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                        <MDBox display="flex" alignItems="center" gap={0.75}>
                          <MDBox sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: item.color, flexShrink: 0 }} />
                          <MDTypography variant="caption">{item.label}</MDTypography>
                        </MDBox>
                        <MDTypography variant="caption" fontWeight="bold">
                          {item.value} ({totalCount ? Math.round((item.value / totalCount) * 100) : 0}%)
                        </MDTypography>
                      </MDBox>
                    ))}
                  </MDBox>
                </MDBox>
              )}
            </Card>
          </Grid>

          {/* Quick Links */}
          <Grid item xs={12} md={6}>
            <Card sx={{ p: 3, borderRadius: 3, height: "100%", boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}>
              <MDTypography variant="h6" fontWeight="bold" mb={2}>{t("headings.quickLinks")}</MDTypography>
              <Stack spacing={0.5}>
                {quickLinks.map((link) => (
                  <MDBox key={link.label} onClick={link.onClick} display="flex" alignItems="center" gap={2}
                    sx={{ p: 1.5, borderRadius: "12px", cursor: "pointer", transition: "all 160ms", "&:hover": { bgcolor: palette.neutral[50] } }}>
                    <MDBox sx={{ width: 40, height: 40, borderRadius: "12px", bgcolor: link.bg, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      <Icon sx={{ color: link.color, fontSize: "20px !important" }}>{link.icon}</Icon>
                    </MDBox>
                    <MDTypography variant="body2" fontWeight="bold" sx={{ flex: 1 }}>{link.label}</MDTypography>
                    <Icon sx={{ color: "text.disabled", fontSize: "18px !important" }}>chevron_right</Icon>
                  </MDBox>
                ))}
              </Stack>
            </Card>
          </Grid>
        </Grid>

        {/* ── Assessment Table ── */}
        <Card sx={{ p: 3, borderRadius: 3, boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}>
          <MDBox display="flex" alignItems="center" gap={1.5} mb={1}>
            <MDBox sx={{ width: 44, height: 44, borderRadius: "12px", bgcolor: `${palette.brand[500]}18`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <Icon sx={{ color: palette.brand[500], fontSize: "22px !important" }}>assignment</Icon>
            </MDBox>
            <MDBox>
              <MDTypography variant="h5" fontWeight="bold" lineHeight={1.2}>{t("headings.assessments")}</MDTypography>
              <MDTypography variant="caption" color="text.secondary">{t("messages.dashboard.acrossAllPrograms")}</MDTypography>
            </MDBox>
          </MDBox>
          <DataTable
            entriesPerPage={false}
            table={tableData}
            loader={loader}
            pageNumber={pageNumber}
            setPageNumber={setPageNumber}
          />
        </Card>
      </MDBox>

      <Snackbar
        autoHideDuration={10000}
        open={openSnackbar}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={handleCloseSnackbar} severity="info" sx={{ width: "100%" }}>
          {t("notifications.noAssessments.title")} {t("notifications.noAssessments.support")}{" "}
          <a href="mailto:support@maxu.co">support@maxu.co</a>{" "}
          {t("notifications.noAssessments.continue")}
        </Alert>
      </Snackbar>

      {showBot && chatQuestions.length > 0 && (
        <Bot
          showUnreadChat={showUnreadChat}
          setShowUnreadChat={setShowUnreadChat}
          showTodayContent={showTodayContent}
          todayContent={todayContent}
          chatQuestions={chatQuestions}
          showIntro={showIntro}
          userMetricsData={userMetricsData}
          setUserMetricsData={setUserMetricsData}
          autoExpandBot={autoExpandBot}
          profile={profile}
        />
      )}
    </DashboardLayout>
  );
}

const LoadingAssessmentDialog = ({ setOpenLoader, openLoader, loadingAssessmentsHandler, t }) => {
  useEffect(() => {
    setTimeout(() => {
      loadingAssessmentsHandler();
      setOpenLoader(false);
    }, 10000);
  }, []);

  return (
    <Dialog open={openLoader} fullScreen>
      <DialogContent>
        <Loader loaderText={t("loaderText.preparingAssessment")} />
      </DialogContent>
    </Dialog>
  );
};
